import { useState, useCallback } from 'react'
import { useAuth } from '@/context/auth-context'
import type { ApiResponse, WebhookPayload } from '@/types'

interface UseApiOptions {
  baseURL?: string
  defaultHeaders?: Record<string, string>
}

interface ExtendedRequestInit extends RequestInit {
  baseURL?: string
  defaultHeaders?: Record<string, string>
}

export function useApi(options: UseApiOptions = {}) {
  const { session } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const makeRequest = useCallback(async <T = unknown>(
    url: string,
    requestOptions: ExtendedRequestInit = {}
  ): Promise<ApiResponse<T>> => {
    setLoading(true)
    setError(null)

    try {
      const baseURL = requestOptions.baseURL || options.baseURL || import.meta.env.VITE_API_BASE_URL || ''
      const fullURL = url.startsWith('http') ? url : `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.defaultHeaders || {}),
        ...(requestOptions.defaultHeaders || {}),
        ...(requestOptions.headers as Record<string, string> || {}),
      }

      // Add auth header if user is authenticated
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`
      }


      const response = await fetch(fullURL, {
        ...requestOptions,
        headers,
      })


      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`)
      }

      return {
        data,
        success: true,
      }
    } catch (err) {
      console.error('💥 useApi - Exception caught:', err)
      console.error('💥 useApi - Error type:', typeof err)
      console.error('💥 useApi - Error message:', err instanceof Error ? err.message : String(err))
      console.error('💥 useApi - Error stack:', err instanceof Error ? err.stack : 'No stack trace')
      
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
      setError(errorMessage)
      return {
        error: errorMessage,
        success: false,
      }
    } finally {
      setLoading(false)
    }
  }, [session, options.baseURL, options.defaultHeaders])

  const get = useCallback(<T = unknown>(url: string, options?: RequestInit) => {
    return makeRequest<T>(url, { ...options, method: 'GET' })
  }, [makeRequest])

  const post = useCallback(<T = unknown>(url: string, data?: unknown, options?: RequestInit) => {
    return makeRequest<T>(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }, [makeRequest])

  const put = useCallback(<T = unknown>(url: string, data?: unknown, options?: RequestInit) => {
    return makeRequest<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }, [makeRequest])

  const del = useCallback(<T = unknown>(url: string, options?: RequestInit) => {
    return makeRequest<T>(url, { ...options, method: 'DELETE' })
  }, [makeRequest])

  return {
    loading,
    error,
    get,
    post,
    put,
    delete: del,
    makeRequest,
  }
}

// Hook specifically for n8n.io webhook calls
export function useWebhooks() {
  const api = useApi()

  const callWebhook = useCallback(async (payload: WebhookPayload) => {
    const { endpoint, method, headers, body } = payload

    return api.makeRequest(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    })
  }, [api])

  return {
    callWebhook,
    loading: api.loading,
    error: api.error,
  }
}
